import request, { getHeaders } from '@/common/request';
import globalStore from '@/store/global';
import { fetchEventSource } from '@/common/fetch-event-source';
import agentStore from '@/store/agentStorage';
import { getChatToken } from "@shared/utils/token";
import axios from 'axios';
import { HelperCore } from '@/common/dependencies';
import userStore from '@/store/user';
import { isEnableEnterpriseKbPage } from '@/components/kb-node-tree/utils'
import { cookie } from '@q/solib-utils';

const get = async (url, params) => {
  try {
    return await request.get(url, { params });
  } catch (error) {
    return error;
  }
};

const post = async (url, data, config) => {
  try {
    return await request.post(url, data, config);
  } catch (error) {
    return error;
  }
};

export const getChatHistory = async params => {
  return await get(`/assistant/conversation/info`, params);
};

export const getChatHeaderInfo = params => {
  return get(`/assistant/conversation/robot`, params);
};

export const createCompletion = async ({
  body,
  signal,
  onopen,
  onmessage,
  onclose,
  onerror,
  onhookBody,
}) => {
  await globalStore.loadCurrentTimestamp();

  let uri = '';
  let needParams = false;

  if(body.ai_agent_id || body.field_category){
    uri = "/ai_agent/chat";
    needParams = true;
  }else if(body.ai_agent_flow_id){
    uri = "/ai_agent_flow/chat";
    needParams = true;
  }else{
    uri = "/assistant/chat";
  }

  if(needParams){
     const params = HelperCore.getParams();
    if(params.agent_uuid){
      body.agent_uuid = params.agent_uuid;
    }
    if(params.agent_ver){
      body.agent_ver = params.agent_ver;
    }
  }

  let headers = getHeaders();
  headers = {
    ...(await getChatToken(`/api${uri}`, body)),
    ...headers,
    ...{
      'Content-Type': 'application/json',
    },
  }

  let url = `${import.meta.env.VITE_API_URL}${uri}`

  if (isEnableEnterpriseKbPage()) {
    // 跨域的SSE无法携带第三方cookie
    url = `${import.meta.env.VITE_SAFE_HOST}/api${uri}`
    headers.Q = encodeURIComponent(cookie.get('Q'))
    headers.T = encodeURIComponent(cookie.get('T'))
  }


  return fetchEventSource(url, {
    method: 'POST',
    headers,
    body,
    signal,
    withCredentials: true,
    openWhenHidden: true,
    onopen: (res, format) => onopen?.(res, format),
    onmessage: msg => onmessage?.(msg),
    onclose: () => onclose?.(),
    onerror: err => onerror?.(err),
    onhookBody,
  });
};

// 删除消息
export function removeMessage(chatId, msg_id) {
  return post('/batch/remove/message', {
    conversation_id: chatId,
    msg_ids: msg_id,
  });
}

// 批量删除所有消息 todo等接口
export function removeAllMessages(chatId) {
  return post('/batch/remove/message', {
    conversation_id: chatId,
    is_all: '1'
  });
}

export async function clearContext(chatId) {
  // 还没有历史会话
  if(!chatId){
    return true;
  }

  try {
    const res = await request.post('/assistant/conversation/mark', {
      conversation_id: chatId,
      msg_type: 'cut',
    });
    return res.code == 0;
  } catch {}
  return false;
}

export async function getSuggestMessages(chatId, messageId, retryTimes = 3) {

  const res = await post('/chat/ask_further', {
    conversation_id: chatId,
    msg_id: messageId,
  });
  if (res.code && retryTimes > 0) {
    await new Promise(resolve => setTimeout(resolve, 600));
    return getSuggestMessages(chatId, messageId, retryTimes - 1)
  }
  return res
}

export function abortEventStream(chatId, messageId) {
  return post('/assistant/chat/stop', {
    conversation_id: chatId,
    message_id: messageId,
  });
}

export const getMindmapXmind = (data, config) => {
  return post("/common/json_x_mind", data, config)
}

export const getChatContext = async ({
  conversation_id = '',
  annex_msg_id = '',
  dataset,
  prompt,
  max_total = 0,
  model_id,
  model_type = 0,
  agent_id,
  role_biz,
  file,
  msg_id,
  is_so,
  ks_id,
  // eslint-disable-next-line
} = params) => {
  try {
    const res = await post('/assistant/chat/context', {
      conversation_id,
      annex_msg_id,
      dataset,
      prompt,
      max_total,
      model_id,
      model_type,
      agent_id: agent_id ||  agentStore.agent_id,
      file,
      role_biz,
      msg_id,
      is_so,
      ks_id,
    }, {
      dataIsJson : true,
    });
    if (res?.code === 4001) {
      userStore.gotoLogin();
      return null;
    }
    return res?.code === 0 && res?.data?.messages ? res.data : null;
  } catch (error) {
    return null;
  }
}

/**
 * 仅支持在"支持未登录访问的渠道"中的智能体
 * 用于浏览器欢迎页进入
 * 后续如果需要推其他智能体，都需要先将智能体发布到该渠道
 */
export const getUnLoginedAgentInfo = params => {
  let url = `${import.meta.env.VITE_AGENT_HOST}/api/v2/nami_agent/welcome_detail`
  return axios.get(url, { params, withCredentials: true});
};

/**
 * 支持所有智能体，要求登录
 */
export const getAgentInfo = params => {
  let url = `${import.meta.env.VITE_AGENT_HOST}/api/v2/nami_agent/detail`
  return axios.get(url, { params, withCredentials: true});
};

// 生图生视频提示词优化使用
export const generateImgVideoCompletion = async ({
  body,
  signal,
  onopen,
  onmessage,
  onclose,
  onerror,
}) => {
  // await globalStore.loadCurrentTimestamp();

  const uri = '/media/optimize_prompt?appsource=so&device_platform=Web';

  let headers = getHeaders();
  headers = {
    ...(await getChatToken(`/api${uri}`, body)),
    ...headers,
    ...{
      'Content-Type': 'application/json',
    },
  }

  return fetchEventSource(`${import.meta.env.VITE_API_URL}${uri}`, {
    method: 'POST',
    headers,
    body,
    signal,
    withCredentials: true,
    openWhenHidden: true,
    onopen: (res, format) => onopen?.(res, format),
    onmessage: msg => onmessage?.(msg),
    onclose: () => onclose?.(),
    onerror: err => onerror?.(err),
  });
};
